import { useEffect, useRef } from "react";

/**
 * Botón oficial de "Iniciar sesión con Google" (Google Identity Services).
 *
 * Carga el script de GIS, renderiza el botón y devuelve el `id_token` (JWT
 * firmado por Google) en `onCredential`. El backend lo verifica en
 * `/auth/web-social`. Si no hay `VITE_GOOGLE_CLIENT_ID` configurado, no renderiza
 * nada (la sección social queda oculta).
 */

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleIdApi {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    ux_mode?: "popup" | "redirect";
    auto_select?: boolean;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

interface Props {
  onCredential: (idToken: string) => void;
  disabled?: boolean;
}

export default function GoogleSignInButton({ onCredential, disabled }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // El padre (Login/Register) crea un `onCredential` nuevo en cada render, y
  // renderiza en cada tecla que escribe el usuario. Si el efecto de abajo
  // dependiera de él, vaciaría y volvería a pintar el iframe de Google con
  // cada carácter: era el "parpadeo" de la pantalla de inicio de sesión.
  // El botón se pinta UNA vez y el callback lee siempre la versión más reciente.
  const onCredentialRef = useRef(onCredential);
  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    if (!CLIENT_ID || !wrapperRef.current) return;

    const render = () => {
      const idApi = window.google?.accounts?.id;
      const el = wrapperRef.current;
      if (!idApi || !el) return;

      idApi.initialize({
        client_id: CLIENT_ID,
        callback: (response) => {
          if (response.credential) onCredentialRef.current(response.credential);
        },
        ux_mode: "popup",
        auto_select: false,
      });

      el.innerHTML = "";
      idApi.renderButton(el, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        logo_alignment: "left",
        locale: "es",
        width: Math.min(el.offsetWidth || 320, 400),
      });
    };

    if (window.google?.accounts?.id) {
      render();
      return;
    }

    let script = document.getElementById(
      "google-gsi-client",
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = "google-gsi-client";
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", render);
    return () => script?.removeEventListener("load", render);
  }, []);

  if (!CLIENT_ID) return null;

  return (
    <div
      ref={wrapperRef}
      className={`flex justify-center ${disabled ? "pointer-events-none opacity-60" : ""}`}
    />
  );
}
